import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarTypes } from 'src/app/core/_enums/snackbar.types';
import { SnackbarService } from 'src/app/core/_services/snackbar.service';
import { ProjectService } from 'src/app/core/_services/project.service';
import { ConfirmCancelDialogComponent } from '../confirm-cancel-dialog/confirm-cancel-dialog.component';

export class ImportProjectDialogData {
  perimeterId?: string;
  navigateAfterImport?: boolean;
}

@Component({
  selector: 'app-dialog-import-project',
  templateUrl: './dialog-import-project.component.html',
  styleUrls: ['./dialog-import-project.component.scss']
})
export class DialogImportProjectComponent {
  dialogData: ImportProjectDialogData = new ImportProjectDialogData();
  selectedFile: File | null = null;
  fileName: string = '';
  formatError: boolean = false;
  isLoading: boolean = false;
  apiError: string = '';

  constructor(
    public importDialogRef: MatDialogRef<DialogImportProjectComponent>,
    private snackbarService: SnackbarService,
    private projectService: ProjectService,
    private router: Router,
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private translate: TranslateService
  ) {
    if (data) {
      this.initialize(data);
    }
  }

  initialize(data: any) {
    this.dialogData.perimeterId = data.perimeterId;
    this.dialogData.navigateAfterImport = data.navigateAfterImport !== false;
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (fileExtension === 'sevetp') {
        this.selectedFile = file;
        this.fileName = file.name;
        this.formatError = false;
        this.apiError = '';
      } else {
        this.formatError = true;
        this.apiError = '';
        this.selectedFile = null;
        this.fileName = '';
        const fileInput = document.getElementById('fileInput') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
      }
    }
  }

  onRemoveFile() {
    this.selectedFile = null;
    this.fileName = '';
    this.formatError = false;
    this.apiError = '';
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  onImport() {
    if (!this.selectedFile) {
      this.snackbarService.open(SnackbarTypes.error, this.translate.instant('error_select_file'));
      return;
    }

    this.isLoading = true;
    this.apiError = '';
    const formData = new FormData();
    formData.append('file', this.selectedFile);

    this.projectService.importProject(formData).subscribe(
      (result: any) => {
        this.isLoading = false;
        if (result.isSuccessful) {
          this.snackbarService.open(SnackbarTypes.add);
          this.importDialogRef.close({ success: true, file: this.selectedFile, projectId: result.importedProjectId });

          if (this.dialogData.navigateAfterImport) {
            setTimeout(() => {
              this.router.navigate(['/project', result.importedProjectId]);
            }, 500);
          }
        } else {
          this.apiError = result.error || this.translate.instant('error_import_project_failed');
        }
      },
      (error) => {
        this.isLoading = false;
        console.error('Import error:', error);
        this.apiError = this.translate.instant('error_import');
      }
    );
  }

  onCancel() {
    if (this.selectedFile) {
      const confirmDialog = this.dialog.open(ConfirmCancelDialogComponent, {
        data: {
          message: this.translate.instant('confirm_cancel_import_project'),
          confirmText: this.translate.instant('yes_cancel_import'),
          cancelText: this.translate.instant('continue_import'),
          confirmColor: 'warn'
        }
      });

      confirmDialog.afterClosed().subscribe((result) => {
        if (result) {
          this.importDialogRef.close();
        }
      });
    } else {
      this.importDialogRef.close();
    }
  }

  canImport(): boolean {
    return this.selectedFile !== null;
  }
}
